import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faArrowDownArrowUp,
  faInfoCircle,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { gql } from 'graphql-tag';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { OperationContext } from 'urql';
import { useClient, useMutation } from 'urql';
import { AppContext } from '~/App';
import { Switch } from '~/components/form/switch/Switch';
import { TextArea as AutoTextArea } from '~/components/form/textarea/TextArea';
import { Confirm } from '~/components/ui/Confirm';
import { Label } from '~/components/ui/Label';
import { Tooltip } from '~/components/ui/Tooltip';
import {
  usePlaceTypeConfigQuery,
  useSitesQuery,
  useSiteTypesQuery,
  useUpdateSiteMutation,
} from '~/generated/graphql';
import { useLocalStorage } from '~/hooks';
import { NavTypesPicker } from '../shared/NavTypesPicker';
import {
  MAX_INFERRED_NAV_ITEMS,
  OTHER_TYPE,
  writeShowAllToStorage,
} from './shared/constants';

import type { SiteInput, Status } from '~/generated/graphql';
import { serializeByKey } from '../shared/serializedPersist';
import {
  arraysEqual,
  deriveTypesFromText,
  isLikelyPluralization,
  uniqueSorted,
} from '../shared/typesConfigUtils';
import {
  applyRenamePairsToNav,
  computeRenamePairs,
  dedupeNormalized,
  listenAndSuppressRenames,
} from '../shared/typesEditUtils';
import { networkOnly, networkOnlyWithSignal } from '../shared/urqlHelpers';
import { useBackgroundTypeUpdater } from '../shared/useBackgroundTypeUpdater';
import { useMultiTypeReassignState } from '../shared/useMultiTypeReassignState';
import { useProgressToast } from '../shared/useProgressToast';
import { useTypesEditingFlow } from '../shared/useTypesEditingFlow';

// (concurrency constant moved into shared hook)
// const EVT_NAV_SHOW_ALL_CHANGED = 'siteNavShowAllChanged';
const EVT_SITE_TYPE_RENAMED = 'siteTypeRenamed';

type OrderModeGql = 'ALPHA' | 'MANUAL';
type PlaceTypeConfig = {
  types: string[];
  orderMode: OrderModeGql;
  navTypes: string[];
  showAllInNav: boolean;
  isCustomized: boolean;
};
type UpdatePlaceTypeConfigInput = {
  types: string[];
  orderMode: OrderModeGql;
  navTypes: string[];
  showAllInNav: boolean;
  isCustomized: boolean;
};
type UpdatePlaceTypeConfigResult = {
  updatePlaceTypeConfig: PlaceTypeConfig & { updatedAt?: string };
};
// (RenamePlaceType types removed; client-side rename implemented instead)

// Shapes for safely handling site attributes without using any
// Note: server expects AttributeInput.name to be the attribute ID
// so prefer id if present, falling back to name
type SiteAttributeLike = { id?: string; name?: string; value?: unknown };
type SiteEntity = {
  id: string;
  type?: string | null;
  status?: Status | null;
  name?: string | null;
  address?: string | null;
  images?: unknown[] | null;
  notes?: string | null;
  licensor?: { id: string } | null;
  defaultSource?: { id: string } | null;
  homhero?: { id: string } | null;
  airbnbCalendar?: Array<{ url: string }> | null;
  airbnbDefaultCheckIn?: string | null;
  airbnbDefaultCheckOut?: string | null;
  attributes?: Array<
    SiteAttributeLike | { id?: string; name?: string; value?: unknown }
  > | null;
};
// type SiteWithAttributesLike = {
//   attributes?: Array<SiteAttributeLike> | null;
// };

// Map site attributes to GraphQL input shape to avoid 400s on update
// IMPORTANT: backend expects AttributeInput.name to reference the attribute id if available.
const mapSiteAttributesInput = (
  attrs: Array<SiteAttributeLike> | null | undefined
): Array<{ name: string; value: string[] }> => {
  if (!attrs) return [];
  const out: Array<{ name: string; value: string[] }> = [];
  for (const a of attrs) {
    const idRaw = (a as { id?: unknown } | undefined)?.id;
    const id = typeof idRaw === 'string' ? idRaw.trim() : '';
    const name = (a?.name ?? '').toString().trim();
    const identifier = id || name;
    if (!identifier) continue;
    // Never submit integration-derived attributes; backend owns them.
    if (identifier.startsWith('integration_')) continue;
    // Skip Guesty-sourced attributes (labels like "Guesty \" or "Guesty Amenities \"),
    // these are integration artifacts and not accepted on update.
    if (/^Guesty(\s|\\)/.test(identifier)) continue;
    const v = (a as { value?: unknown } | undefined)?.value;
    if (Array.isArray(v)) {
      out.push({
        name: identifier,
        value: v.map((x) => (x == null ? '' : String(x))),
      });
    } else if (v == null) {
      out.push({ name: identifier, value: [] });
    } else {
      // If the value is a stringified JSON array, parse it into an array of strings
      if (typeof v === 'string') {
        const trimmed = v.trim();
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          try {
            const parsed = JSON.parse(trimmed);
            if (Array.isArray(parsed)) {
              out.push({
                name: identifier,
                value: parsed.map((x) => (x == null ? '' : String(x))),
              });
              continue;
            }
          } catch {
            // fall through to push as single string below
          }
        }
      }
      out.push({ name: identifier, value: [String(v)] });
    }
  }
  return out;
};

// Build SiteInput consistently with form submission semantics
const buildSiteInputFromEntity = (s: SiteEntity, to: string): SiteInput => {
  const input: SiteInput = {
    type: to,
    status: (s.status || 'Active') as Status,
    name: s.name || '',
  };

  // address: keep as non-empty string; if object-like in future, stringify; otherwise omit
  if (s.address != null) {
    if (typeof s.address === 'string') {
      const trimmed = s.address.trim();
      // Omit meaningless serialized empty object
      if (trimmed.length > 0 && trimmed !== '{}') input.address = trimmed;
    } else if (typeof s.address === 'object') {
      const keys = Object.keys(s.address as Record<string, unknown>);
      if (keys.length > 0) input.address = JSON.stringify(s.address);
    }
  }

  // CRITICAL: Preserve licensor (place owner) - if not included, it gets wiped!
  if (s.licensor?.id) {
    input.licensorId = s.licensor.id;
  }

  // CRITICAL: Preserve defaultSource - if not included, it gets wiped!
  if (s.defaultSource?.id) {
    input.defaultSourceId = s.defaultSource.id;
  }

  // CRITICAL: Preserve homhero integration - if not included, it gets wiped!
  if (s.homhero?.id) {
    input.homheroId = s.homhero.id;
  }

  // CRITICAL: Preserve calendar URLs - if not included, they get wiped!
  if (s.airbnbCalendar && s.airbnbCalendar.length > 0) {
    input.airbnbCalendar = s.airbnbCalendar.map((cal) => cal.url);
  }

  // CRITICAL: Preserve check-in/check-out times - if not included, they get wiped!
  if (s.airbnbDefaultCheckIn) {
    input.airbnbDefaultCheckIn = s.airbnbDefaultCheckIn;
  }
  if (s.airbnbDefaultCheckOut) {
    input.airbnbDefaultCheckOut = s.airbnbDefaultCheckOut;
  }

  const imgs = (s.images as string[] | undefined) ?? [];
  if (imgs.length > 0) input.images = imgs;
  const notes = typeof s.notes === 'string' ? s.notes.trim() : '';
  if (notes.length > 0) input.notes = notes;
  const attrs = mapSiteAttributesInput(s?.attributes);
  if (attrs.length > 0) input.attributes = attrs;
  return input;
};

// (removed unused inline Select component)

// ============================================================================
// Shared GraphQL Query Constants (extracted to avoid duplication)
// ============================================================================

/**
 * Query to fetch all sites with full details for background updates.
 * Used across multiple rename and reassign operations.
 */
const ALL_SITES_FRESH_QUERY = gql`
  query AllSitesFresh {
    sites {
      id
      type
      status
      name
      address
      images
      notes
      licensor {
        id
      }
      defaultSource {
        id
      }
      homhero {
        id
      }
      airbnbCalendar {
        url
      }
      airbnbDefaultCheckIn
      airbnbDefaultCheckOut
      attributes {
        id
        name
        value
      }
    }
  }
`;

/**
 * Query to refresh site type counts cache.
 * Used after type operations to ensure UI shows accurate counts.
 */
const SITE_TYPE_COUNTS_REFRESH_QUERY = gql`
  query SiteTypeCounts_Refresh {
    siteTypeCounts {
      type
      count
    }
  }
`;

/**
 * Query to refresh place type config cache.
 * Used after config updates to ensure UI reflects latest state.
 */
const PLACE_TYPE_CONFIG_REFRESH_QUERY = gql`
  query PlaceTypeConfig_Refresh {
    placeTypeConfig {
      types
      orderMode
      navTypes
      showAllInNav
      isCustomized
    }
  }
`;

/**
 * Helper function to refresh all place-related caches in parallel.
 * This ensures the UI shows accurate data after type operations.
 *
 * @param client - The urql client instance
 * @param context - Optional operation context (defaults to networkOnly)
 * @returns Promise that resolves when all cache refreshes complete
 */
const refreshAllCaches = async (
  client: ReturnType<typeof useClient>,
  context: Partial<OperationContext> = networkOnly
): Promise<void> => {
  try {
    await Promise.all([
      client.query(ALL_SITES_FRESH_QUERY, {}, context).toPromise(),
      client.query(SITE_TYPE_COUNTS_REFRESH_QUERY, {}, context).toPromise(),
      client.query(PLACE_TYPE_CONFIG_REFRESH_QUERY, {}, context).toPromise(),
    ]);
  } catch (err) {
    // Ignore refresh errors; these are background cache updates only
    console.warn('[ManagePlaceTypes] Cache refresh failed:', err);
  }
};

// ============================================================================
// Component
// ============================================================================

export function ManagePlaceTypes({
  onClose,
  onSaveReady,
  useExternalActions = false,
  onDirtyChange,
}: {
  onClose: () => void;
  onSaveReady?: (save: () => void) => void;
  useExternalActions?: boolean;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  const { tenant } = useContext(AppContext);
  const progress = useProgressToast();
  const tenantKey = tenant?.id || 'default';
  const localStorage = useLocalStorage();
  const client = useClient();
  const bg = useBackgroundTypeUpdater<SiteEntity, SiteInput>({
    getType: (e) => e.type,
  });

  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const scrollPos = useRef<number>(0);

  const [{ data: presetData }] = useSiteTypesQuery({
    variables: { preset: true },
  });

  // Load server place type config
  const [configResult] = usePlaceTypeConfigQuery({
    requestPolicy: 'cache-and-network',
  });
  const serverPreset = useMemo(() => presetData?.siteTypes ?? [], [presetData]);
  // All sites to collect any existing types in use so nothing is missing
  const [{ data: allSitesData }] = useSitesQuery();
  const typesInUse = useMemo(
    () =>
      uniqueSorted(
        (allSitesData?.sites ?? [])
          .map((s) => s.type)
          .filter((t): t is string => !!t)
      ),
    [allSitesData]
  );

  // Base configured types from server (fallback to local union if missing)
  const serverTypes: string[] = useMemo(
    () => configResult.data?.placeTypeConfig?.types ?? [],
    [configResult.data?.placeTypeConfig?.types]
  );
  const serverOrderMode: OrderModeGql =
    (configResult.data?.placeTypeConfig?.orderMode as OrderModeGql) ?? 'ALPHA';
  const serverNavTypes: string[] = useMemo(
    () => configResult.data?.placeTypeConfig?.navTypes ?? [],
    [configResult.data?.placeTypeConfig?.navTypes]
  );
  const serverShowAll: boolean =
    configResult.data?.placeTypeConfig?.showAllInNav ?? true;

  // Build known types from all server types + presets + in-use
  const knownTypes = uniqueSorted([
    ...(serverPreset || []),
    ...(typesInUse || []),
  ]).filter((t) => t.toLowerCase() !== 'other');

  // Base list to display:
  // - If saved preset + manual: preserve manual order and append any missing known types
  // - If saved preset + alpha (or unknown): union and sort alpha
  // - Else: show known types
  const baseList = useMemo(() => {
    if (serverTypes.length) {
      // Respect server-provided order; append only additional types that are actually in use
      const extrasInUse = typesInUse.filter(
        (t) => !serverTypes.includes(t) && t.toLowerCase() !== 'other'
      );
      return [...serverTypes, ...uniqueSorted(extrasInUse)];
    }
    return knownTypes;
  }, [serverTypes, knownTypes, typesInUse]);

  const [text, setText] = useState<string>(baseList.join('\n'));
  const [sortMode, setSortMode] = useState<'alpha' | 'manual'>(
    serverOrderMode === 'MANUAL' ? 'manual' : 'alpha'
  );
  // Nav types selection
  const navSaved = serverNavTypes;
  // Current customized nav (empty => not customized)
  const [navTypes, setNavTypes] = useState<string[]>(navSaved);

  // Determine if there are any place types (based on current textarea contents)
  const hasPlaceTypes = useMemo(() => {
    const lines = text
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((t) => t.toLowerCase())
      .filter((t) => t !== 'other');
    return uniqueSorted(lines).length > 0;
  }, [text]);

  // Parse textarea into unique, normalized types using shared helper (preserves manual order)
  const getTypesFromText = useCallback(
    () => deriveTypesFromText(text, sortMode),
    [text, sortMode]
  );

  // Transient flag to hide a just-renamed old type from options until refetch/cache settles
  const [recentlyRenamedFrom, setRecentlyRenamedFrom] = useState<string | null>(
    null
  );
  // Derived options for NavTypesPicker.
  // Use the live-edited textarea types so the dropdown reflects renames immediately
  const navOptions = useMemo(() => {
    const currentTypes = getTypesFromText();
    return uniqueSorted(currentTypes)
      .filter((t) => t.toLowerCase() !== 'other')
      .filter((t) => (recentlyRenamedFrom ? t !== recentlyRenamedFrom : true));
  }, [getTypesFromText, recentlyRenamedFrom]);

  // Inferred default nav types preview (used when no customization is selected)
  const inferredNavTypes = useMemo(() => {
    // Always show first MAX_INFERRED_NAV_ITEMS alphabetical from configured options (ignore presets/in-use)
    const optionsList = [...navOptions];
    if (optionsList.length === 0) return [] as string[];
    const allAlpha = optionsList.sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: 'base' })
    );
    return allAlpha.slice(0, Math.min(MAX_INFERRED_NAV_ITEMS, allAlpha.length));
  }, [navOptions]);

  // Display value for the picker: use customization if present; otherwise preview inferred defaults
  const displayNavTypes = navTypes.length > 0 ? navTypes : inferredNavTypes;
  // Persisted toggle for showing the "All" tab in sub-navigation (read-only here)
  const [showAllNav, setShowAllNav] = useState<boolean>(serverShowAll);
  // Keep textarea and showAll toggle in sync with latest server data on first load or when
  // user hasn't started editing yet. Avoid clobbering user edits.
  const prevBaseListRef = useRef<string[]>(baseList);
  useEffect(() => {
    const prevBase = prevBaseListRef.current.join('\n');
    // If user hasn't typed (text equals previous base or empty), reflect new baseList
    if (text.trim().length === 0 || text === prevBase) {
      setText(baseList.join('\n'));
    }
    prevBaseListRef.current = baseList;
  }, [baseList, text]);
  useEffect(() => {
    setShowAllNav(serverShowAll);
  }, [serverShowAll]);
  useEffect(() => {
    setSortMode(serverOrderMode === 'MANUAL' ? 'manual' : 'alpha');
  }, [serverOrderMode]);
  // Mutation to persist configuration (declared before any usage)
  const UpdatePlaceTypeConfigDocument = useMemo(
    () => gql`
      mutation UpdatePlaceTypeConfig($input: UpdatePlaceTypeConfigInput!) {
        updatePlaceTypeConfig(input: $input) {
          types
          orderMode
          navTypes
          showAllInNav
          isCustomized
          updatedAt
        }
      }
    `,
    []
  );
  // Server-side rename mutation to atomically migrate sites + config
  const RenamePlaceTypeDocument = useMemo(
    () => gql`
      mutation RenamePlaceType($from: String!, $to: String!) {
        renamePlaceType(input: { from: $from, to: $to }) {
          types
          orderMode
          navTypes
          showAllInNav
          isCustomized
          updatedAt
        }
      }
    `,
    []
  );
  // (no server-side rename; client updates in background)
  const [, updateConfig] = useMutation<
    UpdatePlaceTypeConfigResult,
    { input: UpdatePlaceTypeConfigInput }
  >(UpdatePlaceTypeConfigDocument);
  const isPersistingRef = useRef(false);
  const persistConfig = useCallback(
    async (params: {
      types: string[];
      navTypes?: string[];
      orderMode?: 'ALPHA' | 'MANUAL';
      showAllInNav?: boolean;
      isCustomized?: boolean;
    }): Promise<PlaceTypeConfig> => {
      // Derive strictly from params to avoid reading mutable state during flight
      const typesToPersist = params.types;
      const filteredNavTypes = (params.navTypes ?? []).filter((t) =>
        typesToPersist.includes(t)
      );
      const navTypesToUse = filteredNavTypes;
      const orderMode = params.orderMode ?? 'ALPHA';
      const showAll = params.showAllInNav ?? false;
      const isCustomized = params.isCustomized ?? navTypesToUse.length > 0;

      const run = async (): Promise<PlaceTypeConfig> => {
        try {
          isPersistingRef.current = true;
          const result = await updateConfig({
            input: {
              types: typesToPersist,
              orderMode,
              navTypes: navTypesToUse,
              showAllInNav: showAll,
              isCustomized,
            },
          });
          if (!result || result.error || !result.data?.updatePlaceTypeConfig) {
            throw new Error(
              result?.error?.message || 'Failed to persist configuration'
            );
          }
          return result.data.updatePlaceTypeConfig;
        } finally {
          isPersistingRef.current = false;
        }
      };

      return await serializeByKey('place-types', run);
    },
    [updateConfig]
  );
  // Persist and broadcast changes to "Display All" immediately so SitesNav updates
  const handleToggleShowAllNav = useCallback(
    async (next: boolean) => {
      const prev = showAllNav;
      setShowAllNav(next);
      try {
        writeShowAllToStorage('sites', tenantKey, next, localStorage);
      } catch {
        // ignore
      }

      // Avoid persisting while config is still loading and we have no server types yet
      const isConfigLoading =
        (configResult.fetching ?? false) && serverTypes.length === 0;
      if (isConfigLoading) {
        return;
      }

      // Determine safe values to persist. If serverTypes/navTypes are empty due to loading,
      // fall back to the current UI-derived values to avoid wiping tenant config.
      const typesToPersist =
        serverTypes.length > 0 ? serverTypes : getTypesFromText();
      const navFromServerOrUi =
        serverNavTypes.length > 0 ? serverNavTypes : navTypes;
      const isCustomizedValue =
        typeof configResult.data?.placeTypeConfig?.isCustomized === 'boolean'
          ? (configResult.data?.placeTypeConfig?.isCustomized as boolean)
          : navFromServerOrUi.length > 0;
      try {
        await persistConfig({
          types: typesToPersist,
          orderMode: serverOrderMode,
          navTypes: navFromServerOrUi.filter((t) => typesToPersist.includes(t)),
          showAllInNav: next,
          isCustomized: isCustomizedValue,
        });
      } catch (err) {
        // rollback UI and local storage
        setShowAllNav(prev);
        try {
          writeShowAllToStorage('sites', tenantKey, prev, localStorage);
        } catch {
          /* ignore rollback storage errors */
        }
        const message =
          err instanceof Error
            ? err.message
            : 'Failed to update navigation setting.';
        setError(message);
      }
    },
    [
      showAllNav,
      localStorage,
      tenantKey,
      configResult.data?.placeTypeConfig?.isCustomized,
      configResult.fetching,
      serverNavTypes,
      serverTypes,
      serverOrderMode,
      persistConfig,
      getTypesFromText,
      navTypes,
    ]
  );
  // Initialize navTypes once when data arrives; don't overwrite user edits during the session
  const isCustomizedServer =
    (configResult.data?.placeTypeConfig?.isCustomized as
      | boolean
      | undefined) === true;
  const navTypesInitializedRef = useRef(false);
  useEffect(() => {
    if (navTypesInitializedRef.current) return;
    if (isCustomizedServer && navSaved.length > 0) {
      setNavTypes(navSaved);
    }
    navTypesInitializedRef.current = true;
  }, [navSaved, isCustomizedServer]);
  const [errorMessage, setError] = useState<string>('');
  const [infoMessage, setInfo] = useState<string>('');

  const [showNavHelp, setShowNavHelp] = useState(false);
  const navHelpAnchorRef = useRef<HTMLButtonElement | null>(null);

  const [, updateSite] = useUpdateSiteMutation();
  const updateEntityExec = useCallback(
    (vars: { id: string; input: SiteInput }, ctx?: Partial<OperationContext>) =>
      updateSite(vars, ctx),
    [updateSite]
  );
  const [, renameType] = useMutation<
    { renamePlaceType: PlaceTypeConfig & { updatedAt?: string } },
    { from: string; to: string }
  >(RenamePlaceTypeDocument);

  // Save scroll position when opening safeguard dialog
  const rememberScroll = () => {
    if (textAreaRef.current) scrollPos.current = textAreaRef.current.scrollTop;
  };
  const restoreScroll = () => {
    requestAnimationFrame(() => {
      if (textAreaRef.current)
        textAreaRef.current.scrollTop = scrollPos.current;
    });
  };

  // Safeguard dialog state (multi-delete support)
  const multi = useMultiTypeReassignState({
    text,
    sortMode,
    setText: (next) => setText(next),
    getTypesFromText,
  });
  const {
    deletedTypes,
    setDeletedTypes,
    reassignMap,
    setReassignMap,
    newTypeNames,
    setNewTypeNames,
    availableReassignOptions,
  } = multi;
  const [busy, setBusy] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Derived count across all selected deletions
  const deletedCount = useMemo(() => {
    const sites = allSitesData?.sites ?? [];
    return deletedTypes.reduce(
      (sum, d) => sum + sites.filter((s) => (s.type ?? '') === d.type).length,
      0
    );
  }, [deletedTypes, allSitesData]);

  // Live-sync nav chips and options with textarea edits
  // managed by shared hook
  const pendingRemovalRef = useRef<string | null>(null);
  // (No per-site updating suppression needed in multi-delete flow)

  const SUPPRESS_KEY = 'place-types-rename-suppress';
  useEffect(
    () => listenAndSuppressRenames(EVT_SITE_TYPE_RENAMED, SUPPRESS_KEY),
    []
  );

  // Reset pendingRemovalRef on mount to prevent stale state from previous visits
  useEffect(() => {
    pendingRemovalRef.current = null;
  }, []);

  useTypesEditingFlow({
    text,
    sortMode,
    baseList,
    setNavTypes,
    liveDeletionPrompts: true,
    suppressionKeys: [SUPPRESS_KEY],
    getInUseCached: (type) =>
      (allSitesData?.sites ?? []).some((s) => (s.type ?? '') === type),
    liveCheckInUse: async (type, signal) => {
      try {
        const res = await client
          .query(
            gql`
              query SiteTypeCounts_LiveCheck {
                siteTypeCounts {
                  type
                  count
                }
              }
            `,
            {},
            networkOnlyWithSignal(signal)
          )
          .toPromise();
        const normalize = (v: unknown) =>
          (v == null ? '' : String(v)).trim().toLowerCase();
        return (res.data?.siteTypeCounts ?? []).some(
          (c: { type: string; count: number }) =>
            normalize(c.type) === normalize(type) && (c.count ?? 0) > 0
        );
      } catch {
        return false;
      }
    },
    isPersisting: isPersistingRef.current,
    deletedDialogOpen: deletedTypes.length > 0,
    onPromptReassignMulti: (items) => {
      // Save scroll before opening immediate reassign dialog
      rememberScroll();
      setDeletedTypes(items);
    },
  });

  // Compute dirty state to warn on close when there are unsaved edits
  const isDirty = useMemo(() => {
    const currentTypes = deriveTypesFromText(text, sortMode);
    const orderModeNow: OrderModeGql =
      sortMode === 'manual' ? 'MANUAL' : 'ALPHA';
    if (!arraysEqual(currentTypes, serverTypes)) return true;
    if (orderModeNow !== serverOrderMode) return true;
    // Compare nav types as-is (order sensitive). Filter server to visible current types
    const serverNavFiltered = serverNavTypes.filter((t) =>
      currentTypes.includes(t)
    );
    if (!arraysEqual(navTypes, serverNavFiltered)) return true;
    if (showAllNav !== serverShowAll) return true;
    return false;
  }, [
    text,
    sortMode,
    navTypes,
    showAllNav,
    serverTypes,
    serverOrderMode,
    serverNavTypes,
    serverShowAll,
  ]);

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  // use shared isLikelyPluralization imported from utils

  const handleSave = useCallback(async () => {
    setError('');
    setInfo('');
    setIsSaving(true);
    if (isPersistingRef.current) {
      setError('A save is already in progress. Please wait.');
      setIsSaving(false);
      return;
    }

    // Build newTypes based on sort mode (DRY via helper)
    const newTypes = deriveTypesFromText(text, sortMode);

    // Detect simple rename: exactly one removed from what we're showing (baseList) and one added into newTypes
    const removedFromBase = (baseList || []).filter(
      (t) => !newTypes.includes(t)
    );
    const addedToBase = newTypes.filter((t) => !(baseList || []).includes(t));
    // Prefer server-side rename(s) to atomically migrate places and config
    const renamePairs = computeRenamePairs(baseList, newTypes);
    if (renamePairs.length > 0) {
      try {
        const newTypesNormalized = dedupeNormalized(newTypes);
        const navTypesNext = dedupeNormalized(
          applyRenamePairsToNav(
            navTypes.filter((t) => newTypesNormalized.includes(t)),
            renamePairs
          )
        );

        setRecentlyRenamedFrom(
          renamePairs[renamePairs.length - 1]?.from ?? null
        );
        setText(newTypesNormalized.join('\n'));
        setNavTypes(navTypesNext);

        // Persist updated config FIRST for instant UI update
        await persistConfig({
          types: newTypesNormalized,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: navTypesNext,
          showAllInNav: showAllNav,
          isCustomized: navTypesNext.length > 0,
        });

        // Proactively refresh caches (non-blocking)
        void client
          .query(
            gql`
              query PlaceTypeConfig_Nav_Force {
                placeTypeConfig {
                  types
                  orderMode
                  navTypes
                  showAllInNav
                  isCustomized
                }
              }
            `,
            {},
            networkOnly
          )
          .toPromise();
        void client
          .query(
            gql`
              query SiteTypeCounts_ForceRefresh_Multi {
                siteTypeCounts {
                  type
                  count
                }
              }
            `,
            {},
            networkOnly
          )
          .toPromise();

        // Dispatch rename events immediately for optimistic nav update
        try {
          if (typeof window !== 'undefined') {
            for (const { from, to } of renamePairs) {
              window.dispatchEvent(
                new CustomEvent(EVT_SITE_TYPE_RENAMED, { detail: { from, to } })
              );
            }
          }
        } catch {
          /* ignore */
        }

        // Small delay for button feedback, then close optimistically
        await new Promise((resolve) => setTimeout(resolve, 300));
        onDirtyChange?.(false);
        onClose();

        // Background: server rename + site updates with progress
        void (async () => {
          try {
            // Try server-side rename(s) without blocking UI
            for (const { from, to } of renamePairs) {
              try {
                const res = await renameType({ from, to });
                if (!res || res.error || !res.data?.renamePlaceType) {
                  // Non-fatal; continue with client-side updates
                  console.warn(
                    'Server rename failed for pair',
                    { from, to },
                    res?.error
                  );
                }
              } catch (err) {
                console.warn('Server rename threw for pair', { from, to }, err);
              }
            }

            // Fetch fresh sites for accurate totals
            const fresh = await client
              .query(ALL_SITES_FRESH_QUERY, {}, networkOnly)
              .toPromise();
            const freshSites: SiteEntity[] = fresh.data?.sites ?? [];

            // Compute total across all pairs for progress bar
            const normalize = (v: unknown) =>
              (v == null ? '' : String(v)).trim().toLowerCase();
            const totalToUpdate = renamePairs.reduce((sum, { from }) => {
              return (
                sum +
                freshSites.filter((s) => normalize(s.type) === normalize(from))
                  .length
              );
            }, 0);

            progress.start({
              label: 'Updating places…',
              delayMs: 300,
              persistent: true,
            });
            if (totalToUpdate > 0) progress.setTotal(totalToUpdate);

            const buildInput = (
              s: SiteEntity,
              _from: string,
              toVal: string
            ): SiteInput => buildSiteInputFromEntity(s, toVal);

            for (const { from, to } of renamePairs) {
              await bg.runRenameUpdates({
                from,
                to,
                allEntities: freshSites,
                buildInput,
                updateEntity: updateEntityExec,
                progress,
                opId: `rename-${from}-${to}-${Date.now()}`,
                retries: 2,
                managedProgress: true,
                completionMessage: 'Places updated',
                failureMessage: 'Failed to update places',
              });
            }

            // Hook manages completion toast with managedProgress: true

            // Refresh caches after completion
            await refreshAllCaches(client, networkOnly);
          } catch (err) {
            console.error(
              '[ManagePlaceTypes] Background multi-rename failed:',
              err
            );
            // Hook manages error toast with managedProgress: true
          } finally {
            setIsSaving(false);
          }
        })();

        return;
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to rename place types.';
        setError(message);
        return;
      } finally {
        setRecentlyRenamedFrom(null);
      }
    }

    if (removedFromBase.length === 1 && addedToBase.length === 1) {
      const from = removedFromBase[0];
      const to = addedToBase[0];
      try {
        // Optimistically update local UI state (normalize and dedupe)
        setRecentlyRenamedFrom(from);
        setText((prev) => {
          const lines = prev.split('\n');
          let replaced = false;
          const next = lines
            .map((l) => {
              const v = l.trim();
              if (v === from) {
                replaced = true;
                return to;
              }
              return l;
            })
            .filter(Boolean) as string[];
          if (!replaced) next.push(to);
          return deriveTypesFromText(next.join('\n'), sortMode).join('\n');
        });
        const navTypesNext = Array.from(
          new Set(
            navTypes
              .map((t) => (t === from ? to : t))
              .filter((t) => newTypes.includes(t))
          )
        );
        setNavTypes(navTypesNext);

        // Persist updated config FIRST for instant UI update
        await persistConfig({
          types: newTypes,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: navTypesNext,
          showAllInNav: showAllNav,
          isCustomized: navTypesNext.length > 0,
        });

        // Force-refresh counts/config (non-blocking)
        void client
          .query(
            gql`
              query SiteTypeCounts_ForceRefresh_PlaceSingle {
                siteTypeCounts {
                  type
                  count
                }
              }
            `,
            {},
            networkOnly
          )
          .toPromise();

        // Dispatch event immediately so UI updates
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent(EVT_SITE_TYPE_RENAMED, { detail: { from, to } })
            );
          }
        } catch {
          /* ignore */
        }

        // Brief delay for button feedback, then close optimistically
        await new Promise((resolve) => setTimeout(resolve, 300));
        onDirtyChange?.(false);
        onClose();

        // Background updates
        void (async () => {
          try {
            // Best-effort server rename
            try {
              const res = await renameType({ from, to });
              if (!res || res.error || !res.data?.renamePlaceType) {
                console.warn('Server rename failed (single)', res?.error);
              }
            } catch (err) {
              console.warn('Server rename threw (single)', err);
            }

            const fresh = await client
              .query(ALL_SITES_FRESH_QUERY, {}, networkOnly)
              .toPromise();
            const freshSites: SiteEntity[] = fresh.data?.sites ?? [];
            const normalize = (v: unknown) =>
              (v == null ? '' : String(v)).trim().toLowerCase();
            const totalToUpdate = freshSites.filter(
              (s) => normalize(s.type) === normalize(from)
            ).length;
            progress.start({
              label: 'Updating places…',
              delayMs: 300,
              persistent: true,
            });
            if (totalToUpdate > 0) progress.setTotal(totalToUpdate);
            await bg.runRenameUpdates({
              from,
              to,
              allEntities: freshSites,
              buildInput: (
                s: SiteEntity,
                _from: string,
                toVal: string
              ): SiteInput => buildSiteInputFromEntity(s, toVal),
              updateEntity: updateEntityExec,
              progress,
              opId: `rename-${from}-${to}-${Date.now()}`,
              retries: 2,
              managedProgress: true,
              completionMessage: 'Places updated',
              failureMessage: 'Failed to update places',
            });
            // Hook manages completion toast with managedProgress: true
          } catch (err) {
            console.error(
              'Failed to update places after single rename (bg).',
              err
            );
            // Hook manages error toast with managedProgress: true
          } finally {
            setIsSaving(false);
          }
        })();

        return;
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to rename place type.';
        setError(message);
        return;
      } finally {
        setRecentlyRenamedFrom(null);
      }
    }

    // Handle likely pluralization rename even if user hasn't removed the singular yet
    if (removedFromBase.length === 0 && addedToBase.length === 1) {
      const to = addedToBase[0];
      const from = (baseList || []).find((b) => isLikelyPluralization(b, to));
      if (from) {
        try {
          setRecentlyRenamedFrom(from);
          setText((prev) => {
            const lines = prev.split('\n');
            let replaced = false;
            const next = lines
              .map((l) => {
                const v = l.trim();
                if (v === from) {
                  replaced = true;
                  return to;
                }
                return l;
              })
              .filter(Boolean) as string[];
            if (!replaced) next.push(to);
            return deriveTypesFromText(next.join('\n'), sortMode).join('\n');
          });
          const navTypesNext = Array.from(
            new Set(
              navTypes
                .map((t) => (t === from ? to : t))
                .filter((t) => newTypes.includes(t))
            )
          );
          setNavTypes(navTypesNext);

          await persistConfig({
            types: newTypes,
            orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
            navTypes: navTypesNext,
            showAllInNav: showAllNav,
            isCustomized: navTypesNext.length > 0,
          });

          // Non-blocking cache refresh and event
          void client
            .query(
              gql`
                query SiteTypeCounts_ForceRefresh2_PlaceBg {
                  siteTypeCounts {
                    type
                    count
                  }
                }
              `,
              {},
              networkOnly
            )
            .toPromise();
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent(EVT_SITE_TYPE_RENAMED, { detail: { from, to } })
              );
            }
          } catch {
            /* ignore */
          }

          await new Promise((resolve) => setTimeout(resolve, 300));
          onDirtyChange?.(false);
          onClose();

          // Background updates
          void (async () => {
            try {
              // Attempt server rename non-blocking
              try {
                const res = await renameType({ from, to });
                if (!res || res.error || !res.data?.renamePlaceType) {
                  console.warn('Server rename failed (pluralization)');
                }
              } catch (err) {
                console.warn('Server rename threw (pluralization)', err);
              }

              const fresh = await client
                .query(ALL_SITES_FRESH_QUERY, {}, networkOnly)
                .toPromise();
              const freshSites: SiteEntity[] = fresh.data?.sites ?? [];
              progress.start({
                label: 'Updating places…',
                delayMs: 300,
                persistent: true,
              });
              await bg.runRenameUpdates({
                from,
                to,
                allEntities: freshSites,
                buildInput: (
                  s: SiteEntity,
                  _from: string,
                  toVal: string
                ): SiteInput => buildSiteInputFromEntity(s, toVal),
                updateEntity: updateEntityExec,
                progress,
                opId: `rename-${from}-${to}-${Date.now()}`,
                retries: 2,
                managedProgress: true,
                completionMessage: 'Places updated',
                failureMessage: 'Failed to update places',
              });
              // Hook manages completion toast with managedProgress: true
            } catch (err) {
              console.error(
                'Failed to update places after pluralization rename (bg).',
                err
              );
              // Hook manages error toast with managedProgress: true
            } finally {
              setIsSaving(false);
            }
          })();

          return;
        } catch (e: unknown) {
          const message =
            e instanceof Error ? e.message : 'Failed to rename place type.';
          setError(message);
          return;
        } finally {
          setRecentlyRenamedFrom(null);
        }
      }
    }

    // Multi-rename handled above via saveMultiRenamesIfAny

    // Compare with existing base list
    const removed = baseList.filter((t) => !newTypes.includes(t));

    if (removed.length === 0) {
      // No deletions; filter nav types to only current types and persist
      const filteredNavTypes = navTypes.filter((t) => newTypes.includes(t));
      try {
        await persistConfig({
          types: newTypes,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: filteredNavTypes,
          showAllInNav: showAllNav,
          isCustomized: filteredNavTypes.length > 0,
        });
        // Clear dirty state before closing
        onDirtyChange?.(false);
        setIsSaving(false);
        onClose();
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to save configuration.';
        setError(message);
        setIsSaving(false);
      }
      return;
    }

    // Determine which removed types are actually in use right now
    const sites = allSitesData?.sites ?? [];
    const removedInUse = removed.filter((t) =>
      sites.some((s) => (s.type ?? '') === t)
    );

    if (removedInUse.length === 0) {
      // Nothing in use, filter nav types to only current types and persist
      const filteredNavTypes = navTypes.filter((t) => newTypes.includes(t));
      try {
        await persistConfig({
          types: newTypes,
          orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
          navTypes: filteredNavTypes,
          showAllInNav: showAllNav,
          isCustomized: filteredNavTypes.length > 0,
        });
        // Clear dirty state before closing
        onDirtyChange?.(false);
        setIsSaving(false);
        onClose();
      } catch (e: unknown) {
        const message =
          e instanceof Error ? e.message : 'Failed to save configuration.';
        setError(message);
        setIsSaving(false);
      }
      return;
    }

    // Open safeguard for ALL removed, in-use types
    setIsSaving(false);
    rememberScroll();
    const deletionsToHandle = removedInUse.map((type) => ({
      type,
      originalPosition: (baseList || []).indexOf(type),
    }));
    setDeletedTypes(deletionsToHandle);
  }, [
    allSitesData,
    baseList,
    setDeletedTypes,
    navTypes,
    onClose,
    showAllNav,
    sortMode,
    text,
    persistConfig,
    onDirtyChange,
    client,
    progress,
    renameType,
    bg,
    updateEntityExec,
  ]);

  // Expose save handler to parent for external footer action without causing re-renders
  // Keep the latest save function in a ref
  const saveFnRef = useRef<() => void>(() => {});
  useEffect(() => {
    saveFnRef.current = handleSave;
  }, [handleSave]);
  // Provide a stable callback to the parent once
  useEffect(() => {
    if (!onSaveReady) return;
    const stableSave = () => saveFnRef.current();
    onSaveReady(stableSave);
    // onSaveReady is memoized in the parent so this runs once per mount
  }, [onSaveReady]);

  // options are provided by shared hook

  const closeSafeguardLocal = () => {
    if (deletedTypes.length > 0) {
      const current = deriveTypesFromText(text, sortMode);
      const toRestore = deletedTypes.filter((d) => !current.includes(d.type));
      if (toRestore.length > 0) {
        let next: string[];
        if (sortMode === 'alpha') {
          next = uniqueSorted([...current, ...toRestore.map((d) => d.type)]);
        } else {
          next = [...current];
          const sorted = [...toRestore].sort(
            (a, b) => a.originalPosition - b.originalPosition
          );
          for (const d of sorted) {
            const insertAt = Math.min(d.originalPosition, next.length);
            next.splice(insertAt >= 0 ? insertAt : next.length, 0, d.type);
          }
        }
        setText(next.join('\n'));
      }
    }
    pendingRemovalRef.current = null;
    multi.closeSafeguard();
    restoreScroll();
  };

  const performReassign = async () => {
    if (deletedTypes.length === 0) return;
    setBusy(true);
    setError('');
    // Begin delayed progress toast for site updates
    // Compute total upfront so progress bar moves smoothly from 0 → 100%
    // Refetch fresh sites for accurate totals
    const fresh = await client
      .query(ALL_SITES_FRESH_QUERY, {}, networkOnly)
      .toPromise();
    const sitesAll: SiteEntity[] = fresh.data?.sites ?? [];
    const totalToUpdate = deletedTypes.reduce(
      (sum, d) =>
        sum +
        sitesAll.filter((s: SiteEntity) => (s.type ?? '') === d.type).length,
      0
    );
    progress.start({
      label: 'Updating places…',
      delayMs: 300,
      persistent: true,
    });
    if (totalToUpdate > 0) progress.setTotal(totalToUpdate);
    try {
      const current = getTypesFromText();
      const withoutDeleted = current.filter(
        (t) => !deletedTypes.some((d) => d.type === t)
      );
      // Prepare targets for each deletion so we can persist config and close immediately
      const tasks = deletedTypes.map((deletion) => {
        let target = reassignMap[deletion.type] || '';
        if (target === '__create__')
          target = (newTypeNames[deletion.type] || '').trim();
        if (!target) target = OTHER_TYPE;
        console.log(
          `[performReassign] Building task for deletion "${
            deletion.type
          }": reassignMap entry = "${
            reassignMap[deletion.type]
          }", final target = "${target}"`
        );
        return { from: deletion.type, to: target };
      });
      console.log('[performReassign] All tasks:', tasks);
      const newTypesToAdd = new Set<string>(
        tasks
          .map((t) => t.to)
          .filter((t) => t.toLowerCase() !== OTHER_TYPE.toLowerCase())
      );
      const nextPreset = uniqueSorted([
        ...withoutDeleted,
        ...Array.from(newTypesToAdd),
      ]);
      const filteredNavTypes = navTypes.filter((t) => nextPreset.includes(t));
      await persistConfig({
        types: nextPreset,
        orderMode: sortMode === 'manual' ? 'MANUAL' : 'ALPHA',
        navTypes: filteredNavTypes,
        showAllInNav: showAllNav,
        isCustomized: filteredNavTypes.length > 0,
      });

      // Update local UI state, then close the dialog immediately
      setText(nextPreset.join('\n'));
      pendingRemovalRef.current = null;
      setDeletedTypes([]);
      setReassignMap({});
      setNewTypeNames({});
      onDirtyChange?.(false);
      onClose();

      // Background updates for sites after dialog closes via hook
      void (async () => {
        try {
          await bg.runBulkReassignUpdates({
            tasks,
            allEntities: sitesAll,
            buildInput: (s: SiteEntity, from: string, to: string): SiteInput =>
              buildSiteInputFromEntity(s, to),
            updateEntity: updateEntityExec,
            progress,
            opId: `bulk-${Date.now()}`,
            retries: 2,
            managedProgress: true,
            completionMessage: 'Places updated',
            failureMessage: 'Failed to update places',
          });

          // Hook manages completion toast with managedProgress: true

          // Refresh caches after all reassignments complete
          await refreshAllCaches(client, networkOnly);
        } catch (err) {
          console.error(
            '[ManagePlaceTypes] Bulk reassign background updates failed:',
            err
          );
          if (err instanceof Error) {
            console.error('[ManagePlaceTypes] Error details:', {
              message: err.message,
              stack: err.stack,
              cause: (err as Error & { cause?: unknown }).cause,
            });
          }
          // Hook manages error toast with managedProgress: true
        }
      })();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : String(error ?? '');
      setError(
        message ||
          'Failed to reassign places. Some sites may have been updated. Please refresh and verify.'
      );
      progress.error('Failed to update places');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className='flex flex-col gap-3'
      onClick={(e) => {
        // Prevent clicks within the form from bubbling to parent backdrop
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        // Prevent keyboard events (especially backspace) from bubbling to parent dialog
        e.stopPropagation();
      }}
    >
      <p className='mb-4 ml-1 text-sm text-secondary'>
        Customize the types available for Places and their visibility in the
        sub-navigation. Changes will apply across all accounts in your
        organisation.
      </p>

      {/* Nav Types selection */}
      {hasPlaceTypes && (
        <div className='mb-1 flex flex-col gap-1'>
          <div className='flex items-center justify-between'>
            <Label as='span'>Places Sub-Navigation</Label>
            <div className='flex items-center gap-2'>
              <div
                className='relative inline-flex items-center'
                onMouseEnter={() => setShowNavHelp(true)}
              >
                <button
                  type='button'
                  aria-label='Places Sub-Navigation help'
                  className='ml-1 inline-flex h-5 w-5 items-center justify-center text-secondary focus-visible:outline-none'
                  onMouseEnter={() => setShowNavHelp(true)}
                  onMouseLeave={() => setShowNavHelp(false)}
                  onFocus={() => setShowNavHelp(true)}
                  ref={navHelpAnchorRef}
                >
                  <FontAwesomeIcon
                    icon={faInfoCircle as IconProp}
                    className='text-secondary'
                  />
                </button>
                <Tooltip
                  isVisible={showNavHelp}
                  anchorEl={navHelpAnchorRef.current}
                  placement='top'
                  withArrow
                  className='min-w-[300px] max-w-[420px] px-3 py-2 text-left'
                >
                  <span className='block px-1 pt-2 text-base font-medium text-white'>
                    Customize Places Navigation Tabs
                  </span>
                  <span className='mt-1 block px-1 pb-2 text-sm text-white opacity-90'>
                    Unselected categories with places will be grouped under
                    “Other” for easy access. Drag chips to reorder; the nav will
                    use this exact order.
                  </span>
                </Tooltip>
              </div>
            </div>
          </div>
          <NavTypesPicker
            options={navOptions}
            value={displayNavTypes}
            onChange={setNavTypes}
          />{' '}
          <div className='mt-1 pl-1'>
            <Switch
              name='showAllNav'
              label='Display “All” in sub-navigation'
              help={
                'When enabled, an “All” tab appears first in Places sub-navigation.'
              }
              checked={showAllNav}
              onChange={handleToggleShowAllNav}
            />
          </div>
        </div>
      )}

      <AutoTextArea
        label='Place Types'
        ref={textAreaRef}
        className='w-full'
        style={{ paddingBottom: 30 }}
        value={text}
        onChange={(e) => setText((e.target as HTMLTextAreaElement).value)}
        placeholder={'One type per line'}
        rows={6}
        description='Enter each choice on a new line. We will automatically add "Other" if it is needed.'
        data-usetiful='place-types-textarea'
      />
      <div className='flex flex-col items-center gap-2 pb-10'>
        {errorMessage && (
          <div
            role='alert'
            aria-live='assertive'
            className='w-full rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'
          >
            {errorMessage}
          </div>
        )}
        {infoMessage && (
          <div
            role='status'
            aria-live='polite'
            className='w-full rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700'
          >
            {infoMessage}
          </div>
        )}
        <div className='flex w-full flex-col justify-start gap-1'>
          <Label as='span'>
            <FontAwesomeIcon
              icon={faArrowDownArrowUp as IconProp}
              className='text-secondary'
            />{' '}
            Sort Types:
          </Label>
          <span className='ml-1 text-xs text-tertiary'>
            This will only affect the order of the types in this list.
          </span>
        </div>
        <div className='ml-1 flex w-full justify-start gap-2 pt-0.5'>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='radio'
              checked={sortMode === 'alpha'}
              onChange={() => setSortMode('alpha')}
            />
            Alphabetical
          </label>
          <label className='flex items-center gap-2 text-sm'>
            <input
              type='radio'
              checked={sortMode === 'manual'}
              onChange={() => setSortMode('manual')}
            />
            Manual
          </label>
        </div>
      </div>
      {!useExternalActions && (
        <div className='mt-2 flex justify-end gap-3'>
          <button
            type='button'
            className='rounded-md px-4 py-2 text-sm font-medium text-brand-dark hover:text-brand'
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type='button'
            className='rounded-md border border-brand bg-brand px-5 py-2 text-sm font-medium text-white hover:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50'
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      )}

      {deletedTypes.length > 0 && (
        <Confirm
          show={true}
          title={'Reassign Places from deleted types'}
          cancel={'Cancel'}
          confirm={'Reassign & Save'}
          onCancel={() => {
            closeSafeguardLocal();
          }}
          onConfirm={performReassign}
          isLoading={busy}
          isDisabled={deletedTypes.some(
            (d) =>
              reassignMap[d.type] === '__create__' &&
              !(newTypeNames[d.type] || '').trim()
          )}
        >
          <div className='px-2 text-left'>
            <p className='mb-1'>
              There are {deletedCount} places currently assigned to deleted
              types.
            </p>
            <p className='mb-2'>
              Choose a new type for each deleted type. If left blank, affected
              places will be assigned to “Other”.
            </p>
            <div className='mt-2 flex flex-col gap-3'>
              {deletedTypes.map((d) => (
                <div
                  key={d.type}
                  className='rounded-md border border-grey-20 p-3'
                >
                  <div className='flex items-center justify-between'>
                    <div className='text-sm font-medium'>“{d.type}”</div>
                  </div>
                  {availableReassignOptions.length > 0 ? (
                    <>
                      <select
                        className='input mt-3 w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                        value={reassignMap[d.type] || ''}
                        onChange={(e) =>
                          setReassignMap((prev) => ({
                            ...prev,
                            [d.type]: e.target.value,
                          }))
                        }
                      >
                        <option value=''>Select a type</option>
                        {availableReassignOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                        <option value='__create__'>Create New Type…</option>
                      </select>
                      {reassignMap[d.type] === '__create__' && (
                        <input
                          className='input mt-2 w-full rounded-md border border-grey-20 px-3 py-2.5 focus-visible:border-brand focus-visible:outline-none'
                          placeholder='New type name'
                          value={newTypeNames[d.type] || ''}
                          onChange={(e) =>
                            setNewTypeNames((prev) => ({
                              ...prev,
                              [d.type]: e.target.value,
                            }))
                          }
                          aria-invalid={!(newTypeNames[d.type] || '').trim()}
                        />
                      )}
                    </>
                  ) : (
                    <p className='mt-2 text-sm text-secondary'>
                      No other types are available. Affected places will be
                      assigned to “Other”.
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </Confirm>
      )}
    </div>
  );
}
